import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { NoticeActions } from "components/notices/NoticeActions";
import Card from "components/ui/card/Card";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { getSignalTypes } from "lib/types/models";

import BIDS_GRAPHIC_SMALL from "../../../assets/images/bids_graphic_small.png";
import BIDS_GRAPHIC_WIDE from "../../../assets/images/bids_graphic_wide.png";
import LEADS_GRAPHIC_SMALL from "../../../assets/images/leads_graphic_small.png";
import LEADS_GRAPHIC_WIDE from "../../../assets/images/leads_graphic_wide.png";

export default function NoticeCTA({
  rightPosition,
  notice,
  trackingContextName,
}: {
  rightPosition: boolean;
  notice: NoticeDetails;
  trackingContextName: "Notice details panel" | "Notice description";
}) {
  const isOpenTender = notice.stagev2 === NoticesStagesv2.OpenTender;

  const [graphicSmall, graphicWide] = isOpenTender
    ? [BIDS_GRAPHIC_SMALL, BIDS_GRAPHIC_WIDE]
    : [LEADS_GRAPHIC_SMALL, LEADS_GRAPHIC_WIDE];

  const noticeSuppliers = Array.from(
    new Set(notice.lots.flatMap((lot) => lot.suppliers?.map((supplier) => supplier.guid) || [])),
  );

  const title = isOpenTender ? "Qualify this tender" : "Unlock insights";
  const text = isOpenTender
    ? "Looking like a good fit? Unlock buyer insights and extract key requirements from tender documents."
    : "Looking like a good fit? Create Lead to unlock buyer and incumbent supplier insights.";

  return (
    <StyledCard>
      <StyledCardContent>
        <StyledImage
          rightPosition={rightPosition}
          smallGraphic={graphicSmall}
          wideGraphic={graphicWide}
        />
        <Flex gap="8px" flexDirection="column">
          <Text h3 bold>
            {title}
          </Text>
          <Text description>{text}</Text>
        </Flex>
        <NoticeActions
          notice={{
            id: notice.guid,
            name: notice.name ?? "",
            value: notice.value ?? undefined,
            url: notice.url || undefined,
            score: notice.score,
            signalTypes: getSignalTypes(notice.signals ?? []),
            procurementStage: {
              id: notice.procurementStage.id,
              stage: notice.procurementStage.stage ? notice.procurementStage.stage.toString() : "",
            },
            isHidden: notice.isHidden,
            stage: notice.stage ?? "Unknown",
            stageV2: notice.stagev2 || undefined,
            closeDate: notice.closeDate || undefined,
            expiryDate: notice.expiryDate || undefined,
            buyers: notice.buyers.map((buyer) => buyer.guid),
            suppliers: noticeSuppliers,
            leads: notice.leads,
            bid: notice.bid || undefined,
          }}
          contextSource={trackingContextName}
          hideSecondaryActions
          canCreateBidReport={isOpenTender}
        />
      </StyledCardContent>
    </StyledCard>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  border: "1px solid",
  borderRadius: "6px",
  borderColor: theme.colors.sysBorderDecorative,
}));

const StyledCardContent = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "16px",

  img: {
    maxWidth: "100%",
    height: "auto",
  },
});

const StyledImage = styled.img<{
  rightPosition: boolean;
  smallGraphic: string;
  wideGraphic: string;
}>(({ rightPosition, smallGraphic, wideGraphic }) => ({
  content: `url(${rightPosition ? smallGraphic : wideGraphic})`,
  "@media screen and (max-width: 880px)": {
    content: `url(${wideGraphic})`,
  },
}));
